/**
 * NoWorkingMethodScreen — terminal panel rendered when the
 * intersection (device capability ∩ tenant policy ∩ enrolled
 * credentials) yields zero usable sign-in methods.
 *
 * The pre-existing UX rendered a `<TransportPicker>` with every
 * card disabled and the Continue button greyed — a confusing
 * "all options are wrong, click around to figure out why"
 * dead-end. Per the 2026-05-08 incident write-up:
 *
 *   - Azure VM user (no Bluetooth, Windows Hello off, only
 *     hybrid-only credential): saw the picker, had no working
 *     option, eventually got Chrome's "your device can't be used."
 *   - Self-enrolling user on a Linux/Firefox host where the
 *     tenant policy excludes cross-platform AND the device has
 *     no platform authenticator: same dead-end shape.
 *
 * Both reduce to "the user can't proceed AND the cause is
 * structural — re-clicking won't help." The right answer is a
 * dedicated panel that names the cause and surfaces the genuine
 * recovery paths:
 *
 *   1. SETUP — when the cause is a missing platform authenticator
 *      AND the device CAN host one (Windows / macOS / Android
 *      etc.), surface `<setupLink>` so the user can enable
 *      Windows Hello / Touch ID. Pairs with the "I've set it up
 *      — try again" re-probe button so the user doesn't lose
 *      their place.
 *
 *   2. RECOVERY — credential-recovery hygiene flow (the same
 *      backend endpoint the existing /login/help page routes
 *      to). Always offered; for the rare user who genuinely has
 *      no working method, this is the unstuck path.
 *
 *   3. CONTACT ADMIN — when the tenant configures a support
 *      email, the same mailto:... pattern other surfaces use.
 *      Hidden when not configured.
 *
 *   4. DIFFERENT DEVICE — informational only ("if you have
 *      another device with Bluetooth and your phone, sign in
 *      from there instead"). Not a button — there's no way to
 *      teleport the user to a different machine.
 *
 * Reused at sign-in (LoginSurface) and at enrollment
 * (enrollment-page). Consumer passes the cause + the wired
 * callbacks; the component's responsibility is purely
 * presentational.
 */

import * as React from "react";
import {
  ExternalLink,
  KeyRound,
  Lightbulb,
  RotateCw,
  Settings,
  ShieldQuestion,
  Smartphone,
} from "lucide-react";

import type { UaHint } from "@pureid/auth-core";
import {
  invalidateProbeCache,
  openSetupLink,
  platformAuthenticatorSetupLink,
  SETUP_LINK_FALLBACK_DELAY_MS,
} from "@pureid/auth-core";

import { Button } from "../button";
import { CopyButton } from "../copy-button";

/**
 * Why the user is stuck. Drives the body copy + which
 * recovery affordance is foregrounded.
 *
 *   - "no_platform_no_hybrid" — the canonical Azure-VM case.
 *     The device has no platform authenticator AND can't
 *     reach a phone over Bluetooth. Setup link + recovery
 *     are both surfaced.
 *
 *   - "policy_excludes_capable" — the device CAN do something,
 *     but the tenant policy explicitly excludes it (e.g.
 *     `attachment_policy=cross-platform` on a device with
 *     only platform auth available). Setup link is NOT
 *     shown — fixing OS-level config wouldn't help, the
 *     restriction is server-side. Recovery + JIT request
 *     are foregrounded.
 *
 *   - "no_credentials_no_enrollment" — the user is signing
 *     in (not enrolling) and has zero credentials of any
 *     transport that could work here. Either they enrolled
 *     a passkey on a different device, or their credentials
 *     were revoked. Recovery flow is the answer.
 *
 *   - "device_cannot_enroll" — enrollment-time variant of
 *     "no_platform_no_hybrid": user is on a device that
 *     can't produce ANY transport the tenant policy permits.
 *     Block enrollment; same setup-link + recovery options
 *     as the sign-in cousin.
 */
export type NoWorkingMethodCause =
  | "no_platform_no_hybrid"
  | "policy_excludes_capable"
  | "no_credentials_no_enrollment"
  | "device_cannot_enroll"
  /**
   * Sign-in time, the user has enrolled credentials, this
   * device CAN'T satisfy any of them. Distinct from
   * `no_platform_no_hybrid` (which suggests "set up Hello
   * here" — wrong advice for a known user whose credential
   * is bound to a different device's TPM). Distinct from
   * `no_credentials_no_enrollment` (which fires when the
   * device CAN do something, but the user's credentials
   * don't match).
   *
   * The honest remedy: sign in from the original device, or
   * recover. NoWorkingMethodScreen suppresses the setup-link
   * button for this cause and foregrounds the recovery CTA.
   */
  | "credentials_on_other_device";

export interface NoWorkingMethodScreenProps {
  /** Why the screen is rendering. Drives copy + foregrounding. */
  cause: NoWorkingMethodCause;
  /** Sign-in vs enrollment. Drives subtle copy differences
   *  ("can't sign you in" vs "can't enroll a passkey here"). */
  mode: "signin" | "enrollment";
  /** UA hint for the setup-link resolver. When omitted, no
   *  deep-link button is rendered (the helper returns null
   *  on Unknown). */
  ua?: UaHint;
  /** Re-probe callback. Wired by the consumer to call
   *  `probeCapabilities()` again and update its state. The
   *  component invalidates the cache before invoking this. */
  onRetry?: () => void;
  /** Recovery handler — the existing credential-recovery /
   *  hygiene flow entry point. When omitted, the recovery
   *  card is hidden (back-compat; older surfaces keep their
   *  existing escape hatches). */
  onStartRecovery?: () => void;
  /** Tenant support contact for the optional "Contact your
   *  administrator" mailto link. Empty / undefined → no
   *  link; the user falls back to the recovery flow above. */
  supportEmail?: string;
  /** Whether the tenant's PureID QR-channel fallback is live.
   *  When true AND onUseQrSignin is wired AND we're in sign-in
   *  mode, the screen foregrounds a "Sign in with your phone
   *  (QR code — no Bluetooth needed)" CTA as the primary
   *  recovery affordance for users on caBLE-hostile devices.
   *  This is the headline reason the kill-switch feature
   *  exists — users on Azure VM / VDI hit this screen and
   *  need a working alternative, not a recovery flow. */
  tenantQrSigninEnabled?: boolean;
  /** Handler that navigates the user to the QR sign-in
   *  surface (typically `/signin-qr`). Wired by the consumer.
   *  When omitted, the QR CTA isn't rendered even if the
   *  channel is live. */
  onUseQrSignin?: () => void;
  /** Phase G — enrollment-mode counterpart to onUseQrSignin.
   *  When wired AND tenantQrSigninEnabled is true AND mode is
   *  "enrollment", the dead-end screen surfaces a "Save your
   *  passkey via QR" CTA that navigates to /enroll-qr. Distinct
   *  from sign-in QR fallback because the destination ceremony
   *  is registration, not assertion — the user has no passkey
   *  yet on this device. */
  onUseQrEnroll?: () => void;
}

interface CauseCopy {
  title: string;
  body: string;
}

const COPY_BY_CAUSE: Record<
  NoWorkingMethodCause,
  Record<"signin" | "enrollment", CauseCopy>
> = {
  no_platform_no_hybrid: {
    signin: {
      title: "This device can't sign you in right now",
      body: "Your account uses a passkey, but this device doesn't have a built-in passkey unlock (Windows Hello / Touch ID / Android biometric) and can't reach a phone over Bluetooth either. Set up a passkey unlock on this device, or sign in from a different device.",
    },
    enrollment: {
      title: "This device can't enroll a passkey",
      body: "Your tenant uses passkeys, but this device doesn't have a built-in passkey unlock (Windows Hello / Touch ID / Android biometric) and can't reach a phone over Bluetooth either. Set up a passkey unlock here, or finish enrollment from a different device.",
    },
  },
  policy_excludes_capable: {
    signin: {
      title: "This sign-in method isn't enabled for your tenant",
      body: "Your administrator has restricted which kinds of passkeys can be used to sign in, and the methods this device supports aren't on the allowed list. Contact your administrator or use the recovery flow below.",
    },
    enrollment: {
      title: "This device can't enroll under your tenant's policy",
      body: "Your administrator has restricted which kinds of passkeys can be enrolled, and the methods this device supports aren't on the allowed list. Use a different device, plug in a security key, or contact your administrator.",
    },
  },
  no_credentials_no_enrollment: {
    signin: {
      title: "No passkey works here",
      body: "Your account has passkeys registered, but none of them can be used from this device. Sign in from the device where you originally enrolled, or recover your account below.",
    },
    enrollment: {
      title: "Can't enroll a passkey here",
      // Phase G — when the tenant's PureID-QR channel is live, the
      // primary affordance below routes to /enroll-qr (the Card 2
      // phone-side ceremony). The body wording stays neutral so
      // the CTA copy can carry the specific recommendation.
      body: "This device can't run the local enrollment ceremony. If your tenant has phone-QR enrollment turned on, save your passkey on your phone instead — otherwise plug in a security key or use a different device.",
    },
  },
  device_cannot_enroll: {
    signin: {
      // Sign-in shouldn't hit this cause (it's enrollment-
      // specific); included for exhaustiveness.
      title: "This device can't be used to sign in",
      body: "Set up a passkey unlock on this device, or sign in from a different device.",
    },
    enrollment: {
      title: "This device can't be used to enroll a passkey",
      body: "Set up Windows Hello, Touch ID, or another local unlock first. Or finish enrollment from a device that has one available.",
    },
  },
  credentials_on_other_device: {
    signin: {
      // The honest copy for the canonical "I have a passkey,
      // but it's somewhere else" case. Critical NOT to
      // suggest Windows Hello setup — Hello on Device 2 won't
      // give the user access to a credential bound to
      // Device 1's TPM.
      title: "Your passkey isn't on this device",
      body: "Your account has a passkey, but it's set up on a different device — the one you used when you registered. Sign in from there, or recover your account below if that device is gone.",
    },
    enrollment: {
      // Defensive — enrollment surfaces shouldn't reach this
      // cause (no enrolled credentials yet at enrollment).
      title: "Can't enroll a passkey here",
      body: "Use a different device, plug in a security key, or contact your administrator.",
    },
  },
};

export function NoWorkingMethodScreen({
  cause,
  mode,
  ua,
  onRetry,
  onStartRecovery,
  supportEmail,
  tenantQrSigninEnabled,
  onUseQrSignin,
  onUseQrEnroll,
}: NoWorkingMethodScreenProps) {
  const copy = COPY_BY_CAUSE[cause][mode];

  // QR fallback: rendered when the tenant has the PureID QR
  // channel live AND the consumer wired a navigation handler.
  // Sign-in mode → onUseQrSignin (Phase F /signin-qr).
  // Enrollment mode → onUseQrEnroll (Phase G /enroll-qr — Card
  // 2 phone-side passkey creation, the headline recovery path
  // for users on a desktop where Card 1 routing is unreliable).
  const channelLive = tenantQrSigninEnabled === true;
  const showQrSigninFallback =
    mode === "signin" && channelLive && !!onUseQrSignin;
  const showQrEnrollFallback =
    mode === "enrollment" && channelLive && !!onUseQrEnroll;
  const showQrFallback = showQrSigninFallback || showQrEnrollFallback;

  // Setup link is only sensible on capability-gated causes —
  // policy-gated causes won't be fixed by enabling Windows
  // Hello (the server still rejects). For the canonical
  // Azure-VM cause we DO show the setup link; for
  // policy_excludes_capable we explicitly do not.
  const setupLink =
    (cause === "no_platform_no_hybrid" || cause === "device_cannot_enroll") && ua
      ? platformAuthenticatorSetupLink(ua)
      : null;

  // Track whether we've attempted to open the setup link so the
  // fallback hint reveals after SETUP_LINK_FALLBACK_DELAY_MS. The
  // hint exposes the URL for manual copy when the browser /
  // OS silently refuses the custom-scheme navigation (older
  // macOS, Firefox in stricter configs, popup-blocked doc URL).
  const [showFallback, setShowFallback] = React.useState(false);
  const fallbackTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  React.useEffect(() => {
    return () => {
      if (fallbackTimerRef.current !== null) {
        clearTimeout(fallbackTimerRef.current);
      }
    };
  }, []);

  function handleSetupClick() {
    if (!setupLink) return;
    openSetupLink(setupLink);
    if (fallbackTimerRef.current !== null) {
      clearTimeout(fallbackTimerRef.current);
    }
    fallbackTimerRef.current = setTimeout(() => {
      setShowFallback(true);
    }, SETUP_LINK_FALLBACK_DELAY_MS);
  }

  function handleRetryClick() {
    invalidateProbeCache();
    onRetry?.();
  }

  return (
    <div className="space-y-5">
      <div className="text-center space-y-2">
        <div className="mx-auto h-12 w-12 rounded-xl border border-warning/30 bg-warning/10 flex items-center justify-center">
          <KeyRound className="h-6 w-6 text-warning" />
        </div>
        <h2 className="text-base font-semibold">{copy.title}</h2>
        <p className="text-sm text-muted-foreground leading-relaxed">{copy.body}</p>
      </div>

      <div className="flex flex-col gap-2">
        {/*
          QR-fallback CTA — when the tenant has the PureID QR
          channel live, this is the highest-leverage action on
          the dead-end screen: an actual SIGN-IN path, no setup
          gymnastics required. Rendered first (primary variant)
          so a stuck Azure-VM user sees their way out before
          they scroll to "recover access." When the setup link
          ALSO renders (no_platform_no_hybrid + UA known), the
          QR CTA still takes precedence — fixing OS settings is
          a slower path than scanning a QR with their phone.
        */}
        {showQrSigninFallback ? (
          <Button type="button" className="w-full" onClick={onUseQrSignin}>
            <Smartphone className="h-3.5 w-3.5 mr-1.5" />
            Sign in with your phone (no Bluetooth needed)
          </Button>
        ) : null}
        {showQrEnrollFallback ? (
          <Button type="button" className="w-full" onClick={onUseQrEnroll}>
            <Smartphone className="h-3.5 w-3.5 mr-1.5" />
            Save your passkey via QR (no Bluetooth needed)
          </Button>
        ) : null}

        {setupLink ? (
          <Button
            type="button"
            variant={showQrFallback ? "outline" : "default"}
            className="w-full"
            onClick={handleSetupClick}
          >
            <Settings className="h-3.5 w-3.5 mr-1.5" />
            {setupLink.label}
          </Button>
        ) : null}

        {/* Fallback hint surfaced ~1.2s after the deep-link click
            for users whose browser silently refused the custom-
            scheme navigation (older macOS, popup-blocker on the
            doc-URL fallback, hardened Firefox profiles). The
            URL is exposed for manual paste so the user is never
            stranded on "I clicked it and nothing happened."
            Always rendered for `kind: "deep_link"` (where the
            silent-refusal mode is real); doc-fallback URLs open
            in a new tab and the failure mode is the popup
            blocker (also worth a manual copy affordance). */}
        {setupLink && showFallback ? (
          <div className="rounded-lg border border-border/40 bg-muted/15 p-2.5 text-xs leading-relaxed">
            <div className="flex items-start gap-2">
              <span className="text-muted-foreground">
                If your settings didn&rsquo;t open, copy this and paste it
                into your browser&rsquo;s address bar:
              </span>
            </div>
            <div className="mt-1.5 flex items-center gap-1.5">
              <code className="flex-1 truncate font-mono text-xs text-foreground bg-background border border-border/40 rounded px-1.5 py-0.5">
                {setupLink.url}
              </code>
              <CopyButton value={setupLink.url} size="sm" />
            </div>
          </div>
        ) : null}

        {onRetry && setupLink ? (
          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={handleRetryClick}
          >
            <RotateCw className="h-3.5 w-3.5 mr-1.5" />
            I&rsquo;ve set it up — try again
          </Button>
        ) : null}

        {/*
         * P0.12 — "Recover access another way" routes to the
         * credential-recovery flow, which assumes the user has
         * EXISTING credentials to recover. In the enrollment
         * (mode === "enroll") context there's nothing to
         * recover — the user is here BECAUSE they don't have
         * credentials yet — so the affordance is incoherent.
         * Hide it in enrollment mode; keep it on the sign-in
         * surface where it makes sense.
         */}
        {onStartRecovery && mode === "signin" ? (
          <Button
            type="button"
            // Recovery is the catch-all when no other path works.
            // Demote to ghost when a primary action (setup or
            // QR fallback) is already on the screen so the user
            // tries the working path before invoking the lost-
            // passkey flow.
            variant={setupLink || showQrFallback ? "ghost" : "outline"}
            className="w-full"
            onClick={onStartRecovery}
          >
            <ShieldQuestion className="h-3.5 w-3.5 mr-1.5" />
            Recover access another way
          </Button>
        ) : null}

        {supportEmail ? (
          <a
            href={`mailto:${supportEmail}?subject=${encodeURIComponent(
              mode === "signin" ? "Trouble signing in" : "Trouble enrolling a passkey",
            )}`}
            className="inline-flex items-center justify-center gap-1 text-xs text-primary hover:underline"
          >
            Contact your administrator ({supportEmail})
            <ExternalLink className="h-3 w-3" />
          </a>
        ) : null}
      </div>

      <div className="rounded-lg border border-border/40 bg-muted/10 p-3 flex gap-2.5 text-xs leading-relaxed text-muted-foreground">
        <Lightbulb className="h-3.5 w-3.5 mt-0.5 shrink-0" aria-hidden />
        <div className="space-y-1.5">
          <div>
            <strong className="text-foreground">Tip:</strong> If you have
            another device (laptop, phone, tablet) with Bluetooth on and your
            phone unlocked, you can{" "}
            {mode === "signin" ? "sign in" : "finish enrollment"} from there
            instead.
          </div>
          {/*
            Forward guidance: the user is stuck HERE; there's nothing
            actionable on this screen except "go to another device."
            Once they DO get in elsewhere, the right next move is to
            add a backup method so this doesn't happen again. We
            mention it only on the sign-in causes where it's
            coherent — adding a backup AT enrollment time happens via
            the §B account-setup interstitial automatically and
            doesn't need a tip here.
          */}
          {mode === "signin" &&
          (cause === "no_platform_no_hybrid" ||
            cause === "credentials_on_other_device") ? (
            <div>
              <strong className="text-foreground">Avoid this next time:</strong>{" "}
              once you&rsquo;re in, set up a recovery email, recovery codes, or
              an authenticator app in your account settings. Any one of them
              works as a fallback when your passkey isn&rsquo;t available.
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
